import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Map, FileText, Users, Settings, BarChart2, DollarSign, 
  ClipboardList, CalendarDays, Database, MapPin,
  ChevronDown, ChevronRight, Beaker
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  className?: string;
  show?: boolean;
  children?: NavItem[];
}

export default function Sidebar() {
  const { canManageSystemSettings, canViewAuditLogs } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { pathname } = useLocation();

  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [path] // Only allow one menu to be expanded at a time
    );
  };

  // Keep menus expanded if we're on their pages
  React.useEffect(() => {
    if (pathname.startsWith('/admin')) {
      setExpandedItems(['/admin']);
    } else if (pathname.startsWith('/enhanced')) {
      setExpandedItems(['/enhanced']);
    }
  }, [pathname]);

  const navItems: NavItem[] = [
    // Dashboard and Map
    { icon: BarChart2, label: 'Dashboard', path: '/' },
    { icon: Map, label: 'Map', path: '/map' },
    // Enhanced items moved to top level (without User-Management)
    { icon: FileText, label: 'Lots', path: '/enhanced/lots' },
    { icon: FileText, label: 'Sub-Leases', path: '/enhanced/sub-leases' },
    { icon: FileText, label: 'Under-Leases', path: '/enhanced/under-leases' },
    { icon: Users, label: 'Leaseholders', path: '/enhanced/leaseholders' },
    { icon: ClipboardList, label: 'Applicants', path: '/enhanced/applicants' },
    { icon: ClipboardList, label: 'Applications', path: '/enhanced/applications' },
    { icon: DollarSign, label: 'Rental-Contracts', path: '/enhanced/rental-contracts' },
    { icon: DollarSign, label: 'Financials', path: '/enhanced/financials' },
    { icon: CalendarDays, label: 'Meetings', path: '/enhanced/meetings' },
    { icon: ClipboardList, label: 'Market Reviews', path: '/enhanced/market-reviews' },
    { icon: FileText, label: 'Lease EOIs', path: '/enhanced/lease-eois' },
    { icon: FileText, label: 'EOI-Responses', path: '/enhanced/eoi-responses' },
    { icon: FileText, label: 'Projects', path: '/enhanced/projects' },
    { icon: FileText, label: 'Suppliers', path: '/enhanced/suppliers' },
    { icon: ClipboardList, label: 'Audit-Logs', path: '/enhanced/audit-logs', show: canViewAuditLogs },
    { icon: Settings, label: 'User Admin', path: '/admin/settings/user-management', show: canManageSystemSettings }
  ];

  const renderNavItem = (item: NavItem) => {
    // Skip rendering if show is explicitly false
    if (item.show === false) return null;
    
    const isExpanded = expandedItems.includes(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isActive = pathname === item.path || 
      (hasChildren && item.children?.some(child => pathname === child.path));

    return (
      <li key={item.path}>
        {hasChildren ? (
          <div className="space-y-1">
            <button
              onClick={() => toggleExpanded(item.path)}
              className={`w-full flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.label}
              {isExpanded ? (
                <ChevronDown className="ml-auto h-4 w-4" />
              ) : (
                <ChevronRight className="ml-auto h-4 w-4" />
              )}
            </button>
            {isExpanded && (
              <ul className="pl-4 space-y-1">
                {item.children?.map(child => (
                  <li key={child.path}>
                    <NavLink
                      to={child.path}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      <child.icon className="mr-3 h-5 w-5" />
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <NavLink
            to={item.path}
            className={({ isActive }) =>
              `flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : `text-gray-600 hover:bg-gray-50 ${item.className || ''}`
              }`
            }
          >
            <item.icon className="mr-3 h-5 w-5" />
            {item.label}
          </NavLink>
        )}
      </li>
    );
  };

  return (
    <aside className="hidden lg:flex h-[calc(100vh-5rem)] w-64 flex-col fixed left-0 top-20 bg-white border-r border-gray-200 z-20">
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {navItems.map(renderNavItem)}
        </ul>
      </nav>
    </aside>
  );
}
